import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography, Divider } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import { MIconButton } from '../../components/@material-extend';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import { AddTopicDialog } from './AddTopicDialog';

export default function CreatePopover() {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const activeOrgGuid = useSelector((s: RootState) => s.organizationContext.activeOrgGuid);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [addTopicOpen, setAddTopicOpen] = useState(false);
  const open = Boolean(anchor);

  const handleClose = () => setAnchor(null);

  return (
    <>
      <MIconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={translate('overview.create')}
        aria-haspopup="menu"
        aria-expanded={open}
        color="default"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: { xs: 40, sm: 'auto' },
          height: 40,
          border: '1px solid',
          borderColor: open ? 'divider' : 'transparent',
          borderRadius: 1,
          '&:hover': {
            border: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent'
          }
        }}
      >
        <AddIcon sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ display: { xs: 'none', sm: 'block' } }}>
          {translate('overview.create')}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: 16,
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: (theme) =>
              theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest
              })
          }}
        />
      </MIconButton>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        {!activeOrgGuid && (
          <>
            <MenuItem
              onClick={() => {
                handleClose();
                setAddTopicOpen(true);
              }}
            >
              <ListItemIcon>
                <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={translate('overview.create_topic_label')}
                secondary={translate('overview.add_topic_subtitle')}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: { fontWeight: (theme) => theme.typography.fontWeightMedium }
                  },
                  secondary: { variant: 'caption' }
                }}
              />
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        <MenuItem
          onClick={() => {
            handleClose();
            navigate(activeOrgGuid ? PATH_ORG.pathwaysCreate : PATH_DASHBOARD.pathway.new);
          }}
        >
          <ListItemIcon>
            <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary={translate('overview.create_pathway_label')}
            secondary={translate('overview.new_pathway_subtitle')}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'caption' } }}
          />
        </MenuItem>
      </Menu>
      <AddTopicDialog open={addTopicOpen} onClose={() => setAddTopicOpen(false)} />
    </>
  );
}
